import { createContext } from "preact";
import { useContext } from "preact/hooks";
import { ApiError } from "../api";
import { errorCodeOf } from "./apiError";
import { DATE_LOCALE, type Lang } from "./i18n";

/**
 * What the console knows about the workspace's plan.
 *
 * The shape comes from `/agent/me` (chat-server `describeWorkspacePlan`), and
 * every screen reads it from one context rather than fetching it again: the
 * plan decides what is drawn (upgrade prompts, disabled features, the
 * suspended screen), so it must be the same answer everywhere on the page.
 *
 * Refusals are the other half. The server answers a plan refusal with
 * 402 `{ code: "plan_limit", limit }` everywhere, so one mapper turns any
 * failed call into "which limit did this hit", and the caller shows the
 * shared notice instead of a bare error message.
 */

/** "unlimited" is the platform owner's own workspace's plan: shown where it applies, never offered or assignable here. */
export type PlanName = "free" | "starter" | "team" | "unlimited";

export type PlanFeature = "themes" | "cannedReplies" | "verifiedIdentity" | "ticketWebhook" | "auditLog";

/** What a 402 can point at: the two counted limits, plus every feature flag. */
export type PlanLimitKind = "sites" | "operators" | PlanFeature;

const LIMIT_KINDS: readonly PlanLimitKind[] = [
  "sites",
  "operators",
  "themes",
  "cannedReplies",
  "verifiedIdentity",
  "ticketWebhook",
  "auditLog",
];

export interface WorkspacePlan {
  /** In effect right now — what the server enforces (a suspended or lapsed Starter reads "free"). */
  plan: PlanName;
  /** The stored column, normalized to a current id, so "Starter — payment overdue" can be said out loud. */
  storedPlan: PlanName;
  planStatus: string;
  limits: { maxSites: number | null; maxOperators: number | null; historyDays: number | null };
  features: Record<PlanFeature, boolean>;
  usage: { sites: number; operators: number };
  inGrace: boolean;
  graceUntil: string | null;
  blocked: boolean;
  currentPeriodEnd?: string | null;
}

/**
 * Stripe's side of the same story; `GET /billing`, owner only.
 *
 * It is deliberately not a WorkspacePlan: that route answers plan, usage and
 * limits but no feature flags and no `blocked`, because the billing screen
 * asks about money, not about what the plan unlocks (the features come with
 * /agent/me, through PlanContext).
 */
export interface BillingState extends Omit<WorkspacePlan, "features" | "blocked" | "currentPeriodEnd"> {
  configured: boolean;
  currentPeriodEnd: string | null;
  /** Flat monthly price per workspace, in cents. */
  prices: { starter: number; team: number };
  hasSubscription: boolean;
}

export interface PlanState {
  /** Null until /agent/me answers, or on a server too old to send it. */
  plan: WorkspacePlan | null;
  isOwner: boolean;
  /** Re-reads /agent/me, e.g. after a checkout. */
  reload: () => void;
}

export const PlanContext = createContext<PlanState>({ plan: null, isOwner: false, reload: () => undefined });

export function usePlan(): PlanState {
  return useContext(PlanContext);
}

/**
 * Which plan limit a failed call hit, or null if it was any other failure.
 *
 * A `plan_limit` with a limit name this build does not know about also reads
 * as null, so an older console shows the server's own message rather than an
 * upgrade prompt for something it cannot name.
 */
export function planLimitOf(err: unknown): PlanLimitKind | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  if (err.body["code"] !== "plan_limit") return null;
  const limit = err.body["limit"];
  return LIMIT_KINDS.find((kind) => kind === limit) ?? null;
}

/** The stable `code` a server refusal carries, when it carries one. */
export function errorCode(err: unknown): string | null {
  return errorCodeOf(err);
}

/**
 * The cheapest plan that includes a feature — the one the hint next to a
 * locked control names. Mirrors the plan table in `lib/chat-db/src/plans.ts`,
 * where themes and canned replies are on every plan and the three that touch
 * a host's own systems are Team's alone.
 */
export function planFor(feature: PlanFeature): PlanName {
  return feature === "themes" || feature === "cannedReplies" ? "free" : "team";
}

/** How full a limit is, 0..1. An unlimited limit is never full. */
export function usageFraction(used: number, limit: number | null): number {
  if (limit === null || limit <= 0) return 0;
  return Math.min(1, used / limit);
}

/** Cents to money. Stripe prices are USD per workspace per month. */
export function money(cents: number, lang: Lang): string {
  return new Intl.NumberFormat(DATE_LOCALE[lang], { style: "currency", currency: "USD" }).format(cents / 100);
}

/** The flat monthly price of a paid plan, or null for free/unknown. */
export function priceOf(prices: { starter: number; team: number }, plan: string): number | null {
  if (plan === "starter") return prices.starter;
  if (plan === "team") return prices.team;
  return null;
}
